/**
 * Medications Thunks
 * All async operations for the medication reconciliation system.
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchEMRSupplementsApi,
  fetchEMRDispensedApi,
  fetchActiveDispensedApi,
  fetchPatientSupplementsApi,
  fetchCustomMedicationsApi,
  addMedicationApi,
  patchEMRSupplementApi,
  patchEMRDispensedApi,
  fetchDefaultItemsApi,
  fetchAllSupplementsApi,
} from '../api/medicationsApi';
import {
  optimisticAddMedication,
  rollbackOptimisticMedication,
  updateEMRItemLocally,
} from '../slices/medicationsSlice';
import { planMedicationSync } from '../../utils/medicationSyncPlan';

// ─── Load All Sources ──────────────────────────────────────────────────────────

/**
 * Loads all 5 medication data sources in parallel for a patient.
 * Call once when a patient tab is opened.
 */
export const loadAllMedicationSources = createAsyncThunk(
  'medications/loadAll',
  async ({ patientId, customerId }, { rejectWithValue }) => {
    try {
      const [
        emrSupsRes,
        emrDispRes,
        activeDispRes,
        patientSupsRes,
        customMedsRes,
      ] = await Promise.all([
        fetchEMRSupplementsApi(patientId).catch(() => ({ data: { supplements: [] } })),
        fetchEMRDispensedApi(patientId).catch(() => ({ data: { dispensed: [] } })),
        fetchActiveDispensedApi(customerId).catch(() => ({ data: [] })),
        fetchPatientSupplementsApi(customerId).catch(() => ({ data: { orderSupplements: [], patientPlanSupplements: { recommended: [], historical: [] } } })),
        fetchCustomMedicationsApi(patientId).catch(() => ({ data: [] })),
      ]);

      return {
        patientId,
        emrSupplements: emrSupsRes.data?.supplements || [],
        emrDispensed: emrDispRes.data?.dispensed || [],
        activeDispensed: activeDispRes.data || [],
        orderSupplements: patientSupsRes.data?.orderSupplements || [],
        patientPlanMeds: patientSupsRes.data?.patientPlanSupplements?.recommended || [],
        historicalMeds: patientSupsRes.data?.patientPlanSupplements?.historical || [],
        customMedications: customMedsRes.data || [],
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Add Custom Medication (Optimistic) ───────────────────────────────────────

/**
 * Adds a custom medication.
 * Optimistically pushes to state immediately, rolls back on failure.
 */
export const addCustomMedication = createAsyncThunk(
  'medications/addCustom',
  async ({ patientId, supplementName, frequency }, { dispatch, rejectWithValue }) => {
    const tempId = `temp_${Date.now()}`;
    const optimisticItem = {
      _id: tempId,
      supplementName: supplementName.trim(),
      frequency: frequency.trim(),
      isCustom: true,
      createdAt: new Date().toISOString(),
    };

    // Optimistic update
    dispatch(optimisticAddMedication({ patientId, item: optimisticItem }));

    try {
      const res = await addMedicationApi(patientId, {
        supplementName: supplementName.trim(),
        frequency: frequency.trim(),
        isCustom: true,
      });
      return { patientId, medications: res.data.medications, tempId };
    } catch (err) {
      // Rollback
      dispatch(rollbackOptimisticMedication({ patientId, tempId }));
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Toggle Check ──────────────────────────────────────────────────────────────

/**
 * Toggles checked state of an EMR supplement or dispensed item.
 * Immediately updates Redux, then PATCHes the server.
 * type: 'supplement' | 'dispensed'
 */
export const toggleMedicationCheck = createAsyncThunk(
  'medications/toggleCheck',
  async ({ patientId, item, checked, type }, { dispatch, rejectWithValue }) => {
    const now = new Date().toISOString();
    const updatedItem = {
      ...item,
      checked,
      status: checked ? 'active' : item.status,
      lastModified: now,
      source: item.source || 'manual',
    };

    // Immediate local update
    dispatch(updateEMRItemLocally({ patientId, item: updatedItem, type }));

    try {
      const patchFn = type === 'supplement' ? patchEMRSupplementApi : patchEMRDispensedApi;
      await patchFn(patientId, {
        name: item.name,
        checked,
        status: checked ? 'active' : item.status,
        frequency: item.frequency || '',
        selectedField: item.selectedField || '',
        source: item.source || 'manual',
      });
      return { patientId };
    } catch (err) {
      // Rollback
      dispatch(updateEMRItemLocally({ patientId, item, type }));
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Move To History ───────────────────────────────────────────────────────────

export const moveMedicationToHistory = createAsyncThunk(
  'medications/moveToHistory',
  async ({ patientId, item, type }, { dispatch, rejectWithValue }) => {
    const now = new Date().toISOString();
    const updatedItem = {
      ...item,
      status: 'historical',
      checked: false,
      endDate: now,
      lastModified: now,
    };

    dispatch(updateEMRItemLocally({ patientId, item: updatedItem, type }));

    try {
      const patchFn = type === 'supplement' ? patchEMRSupplementApi : patchEMRDispensedApi;
      await patchFn(patientId, {
        name: item.name,
        checked: false,
        status: 'historical',
        frequency: item.frequency || '',
        selectedField: item.selectedField || '',
        source: item.source || 'manual',
        endDate: now,
      });
      return { patientId };
    } catch (err) {
      dispatch(updateEMRItemLocally({ patientId, item, type }));
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Restore From History ──────────────────────────────────────────────────────

export const restoreFromHistory = createAsyncThunk(
  'medications/restoreFromHistory',
  async ({ patientId, item, type }, { dispatch, rejectWithValue }) => {
    const updatedItem = {
      ...item,
      status: 'active',
      checked: true,
      endDate: null,
      lastModified: new Date().toISOString(),
    };

    dispatch(updateEMRItemLocally({ patientId, item: updatedItem, type }));

    try {
      const patchFn = type === 'supplement' ? patchEMRSupplementApi : patchEMRDispensedApi;
      await patchFn(patientId, {
        name: item.name,
        checked: true,
        status: 'active',
        frequency: item.frequency || '',
        selectedField: item.selectedField || '',
        source: item.source || 'manual',
        endDate: null,
      });
      return { patientId };
    } catch (err) {
      dispatch(updateEMRItemLocally({ patientId, item, type }));
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Add Order Item to EMR ─────────────────────────────────────────────────────

/**
 * Adds an order supplement/dispensed item into the EMR tracked list.
 * Optimistic: immediately appears in active list.
 * type: 'supplement' | 'dispensed'
 */
export const addOrderItemToEMR = createAsyncThunk(
  'medications/addOrderItem',
  async ({ patientId, item, type }, { dispatch, rejectWithValue }) => {
    const now = new Date().toISOString();
    const newItem = {
      ...item,
      checked: true,
      status: 'active',
      source: 'order',
      startDate: now,
      lastModified: now,
    };

    dispatch(updateEMRItemLocally({ patientId, item: newItem, type }));

    try {
      const patchFn = type === 'supplement' ? patchEMRSupplementApi : patchEMRDispensedApi;
      await patchFn(patientId, {
        name: item.name,
        checked: true,
        status: 'active',
        frequency: item.frequency || '',
        selectedField: item.selectedField || '',
        source: 'order',
        startDate: now,
      });
      return { patientId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Sync Orders With EMR (reconciliation) ────────────────────────────────────

/**
 * Mirrors old EMR's syncOrderItemsWithEMR exactly.
 *
 * For supplements and dispensed items (see utils/medicationSyncPlan):
 *   1. Fetch fresh EMR + order data
 *   2. Orphaned items (source='order', active, but no longer in orders) → historical
 *   3. New order items not in EMR → auto-add checked:true, source:'order'
 *   4. Historical/unchecked items → re-activated only when an order re-added them
 *      (dated or created after the item ended, or the sync itself ended it)
 *   5. Reload Redux state with fresh data
 */
export const syncOrdersWithEMR = createAsyncThunk(
  'medications/syncOrders',
  async ({ patientId, customerId }, { rejectWithValue }) => {
    try {
      // A patient with no EMR list yet answers 404 (an empty list). Any other failure
      // stops the sync: planned from a list that failed to load, it would end every
      // active order item, or bring back items a clinician had ended.
      const emptyIfNone = (empty) => (err) => {
        if (err?.response?.status === 404) return { data: empty };
        throw err;
      };
      const [emrSupsRes, emrDispRes, activeDispRes, orderSupsRes] = await Promise.all([
        fetchEMRSupplementsApi(patientId).catch(emptyIfNone({ supplements: [] })),
        fetchEMRDispensedApi(patientId).catch(emptyIfNone({ dispensed: [] })),
        fetchActiveDispensedApi(customerId),
        fetchPatientSupplementsApi(customerId),
      ]);

      // What to write, worked out in one place for EMRv2 and the old EMR. Nothing when
      // the lists already mirror the orders.
      const plan = planMedicationSync({
        emrSupplements: emrSupsRes.data?.supplements || [],
        emrDispensed: emrDispRes.data?.dispensed || [],
        orderSupplements: orderSupsRes.data?.orderSupplements || [],
        orderDispensed: activeDispRes.data || [],
      });
      for (const body of plan.supplements) {
        await patchEMRSupplementApi(patientId, body).catch(() => {});
      }
      for (const body of plan.dispensed) {
        await patchEMRDispensedApi(patientId, body).catch(() => {});
      }

      // ── Reload fresh state after all patches ─────────────────────────────
      const [freshSups, freshDisp] = await Promise.all([
        fetchEMRSupplementsApi(patientId).catch(emptyIfNone({ supplements: [] })),
        fetchEMRDispensedApi(patientId).catch(emptyIfNone({ dispensed: [] })),
      ]);

      return {
        patientId,
        emrSupplements: freshSups.data?.supplements || [],
        emrDispensed: freshDisp.data?.dispensed || [],
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Load Default Catalog (global, loaded once) ───────────────────────────────

/**
 * Loads the global supplement/dispensed catalog for the modal.
 * Enriches each supplement with dosages + frequencyOptions from getAllSupplements.
 * Call once per app session (slice guards against re-fetching).
 */
export const loadDefaultItems = createAsyncThunk(
  'medications/loadDefaults',
  async (_, { rejectWithValue }) => {
    try {
      const [defaultRes, allSupsRes] = await Promise.all([
        fetchDefaultItemsApi(),
        fetchAllSupplementsApi(),
      ]);

      const fullSupplements = allSupsRes.data || [];
      const enrichedSupplements = (defaultRes.data?.supplements || []).map((item) => {
        const full = fullSupplements.find((s) => s.supplement_name === item.name);
        return {
          ...item,
          dosages: full?.dosages || [],
          frequencyOptions: full?.frequency || [],
        };
      });

      return {
        supplements: enrichedSupplements,
        dispensed: defaultRes.data?.dispensed || [],
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// ─── Update Detail (date/frequency/dosage) ────────────────────────────────────

/**
 * Updates a single field on an EMR item.
 * Debounce the dispatch in the component, not here.
 * type: 'supplement' | 'dispensed'
 */
export const updateMedicationDetail = createAsyncThunk(
  'medications/updateDetail',
  async ({ patientId, item, field, value, type }, { dispatch, rejectWithValue }) => {
    const updatedItem = { ...item, [field]: value, lastModified: new Date().toISOString() };

    dispatch(updateEMRItemLocally({ patientId, item: updatedItem, type }));

    try {
      const patchFn = type === 'supplement' ? patchEMRSupplementApi : patchEMRDispensedApi;
      await patchFn(patientId, {
        name: item.name,
        checked: item.checked,
        status: item.status,
        frequency: field === 'frequency' ? value : (item.frequency || ''),
        selectedField: field === 'selectedField' ? value : (item.selectedField || ''),
        source: item.source || 'manual',
        startDate: field === 'startDate' ? value : item.startDate,
        endDate: field === 'endDate' ? value : item.endDate,
      });
      return { patientId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);
